import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  dismissBatch,
  getUploadSnapshot,
  retryFailed,
  subscribeUploads,
  useUploadBatches,
  type UploadBatch,
} from '../uploadManager';
import type { UploadItem } from '../uploadManager';
import { UploadFileRow } from './UploadFileRow';
import { ME_KEY } from '@/features/auth/hooks';

/** How long a batch that landed cleanly stays in the tray before it clears itself. Batches that
 *  need a look (a failure, a skipped file, a replaced document) stay until dismissed. */
const CLEAN_BATCH_LINGER_MS = 8000;

/**
 * The bottom-right uploads panel: progress for every batch the upload manager is running, from
 * any page. Mounted once in the root layout so the person can leave the vault and keep working
 * while files land. Nothing is rendered while there is nothing to show.
 */
export function UploadTray() {
  // Belt to cancelAllUploads' braces: the tray never draws for someone who is not signed in.
  // Read from the cache, never fetched — the login page must make no authed call. A session
  // that simply expired leaves no identity behind either, so this covers that too.
  const signedIn = useQueryClient().getQueryData(ME_KEY) != null;
  const batches = useUploadBatches();
  const running = batches.some((b) => b.running);
  const [open, setOpen] = useState(false);

  // The panel opens itself when a batch starts — the dialog has just closed, so the person sees
  // at once where their files went — and again when a batch settles needing a look. Each batch
  // does this once per event; in between, the person's own collapse stands.
  const seen = useRef(new Set<string>());
  useEffect(() => subscribeUploads(() => {
    let reopen = false;
    for (const b of getUploadSnapshot().batches) {
      const key = b.running ? `${b.id}:start` : b.needsReview ? `${b.id}:review` : null;
      if (key && !seen.current.has(key)) { seen.current.add(key); reopen = true; }
    }
    if (reopen) setOpen(true);
  }), []);

  // The bytes leave from this tab: closing it abandons whatever has not reached storage yet.
  useEffect(() => {
    if (!running) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers read this; modern ones show their own generic wording.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  // Clean batches clear themselves; the success toast already said what landed.
  const cleanKey = batches.filter((b) => !b.running && !b.needsReview).map((b) => b.id).join('|');
  useEffect(() => {
    if (!cleanKey) return;
    const ids = cleanKey.split('|');
    const t = setTimeout(() => ids.forEach((id) => dismissBatch(id)), CLEAN_BATCH_LINGER_MS);
    return () => clearTimeout(t);
  }, [cleanKey]);

  if (!signedIn || batches.length === 0) return null;

  const items = batches.flatMap((b) => b.items);
  // "Landed" = the bytes have reached storage (finalize is bookkeeping after that), or the row
  // has failed — the progress a person is waiting on.
  const landed = items.filter((it) => it.status === 'done' || it.status === 'failed' || it.status === 'finalizing').length;
  const done = items.filter((it) => it.status === 'done').length;
  const failed = items.filter((it) => it.status === 'failed').length;
  // Everything that happened besides landing or failing: files the import passed over, and
  // copies it retired. Both change what is in the vault, so neither should need a click to see.
  const skipped = items.reduce((n, it) => n + (it.skippedCount ?? 0), 0);
  // From an ARCHIVE import's summary only — a folder upload never replaces anything.
  const replaced = items.reduce((n, it) => n + (it.replacedCount ?? 0), 0);

  // One line that says how it went, and one colour that says whether to look.
  const outcome: 'running' | 'failed' | 'attention' | 'done' =
    running ? 'running'
      : failed > 0 ? 'failed'
        : (skipped > 0 || replaced > 0) ? 'attention'
          : 'done';
  const extras = [
    skipped > 0 ? `${skipped} skipped` : null,
    replaced > 0 ? `${replaced} replaced` : null,
    failed > 0 ? `${failed} failed` : null,
  ].filter(Boolean).join(' · ');
  const summary = running
    ? `Uploading ${landed} of ${items.length}…`
    : `${done} uploaded${extras ? ` · ${extras}` : ''}`;
  const summaryTone = {
    running: 'text-navy',
    done: 'text-state-success-text',
    attention: 'text-state-warning-text',
    failed: 'text-state-error-text',
  }[outcome];

  return (
    <aside
      aria-label="Uploads"
      className="fixed right-4 bottom-4 z-40 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-line bg-card shadow-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Hide uploads' : 'Show uploads'}
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-line-soft/40"
      >
        {outcome === 'running' ? <Loader2 className="size-4 shrink-0 animate-spin text-electric" aria-hidden />
          : outcome === 'failed' ? <XCircle className="size-4 shrink-0 text-state-error-accent" aria-hidden />
            : outcome === 'attention' ? <AlertTriangle className="size-4 shrink-0 text-state-warning-text" aria-hidden />
              : <CheckCircle2 className="size-4 shrink-0 text-state-success-accent" aria-hidden />}
        <span className={cn('min-w-0 flex-1 truncate text-[13px] font-semibold', summaryTone)} aria-live="polite">
          {summary}
        </span>
        {open ? <ChevronDown className="size-4 text-muted" aria-hidden /> : <ChevronUp className="size-4 text-muted" aria-hidden />}
      </button>
      {running && (
        <div className="h-0.5 w-full bg-line-soft" aria-hidden>
          <div
            className="h-full bg-electric transition-[width] duration-300"
            style={{ width: `${items.length ? Math.round((landed / items.length) * 100) : 0}%` }}
          />
        </div>
      )}
      {open && (
        <div className="max-h-[50vh] space-y-3 overflow-y-auto border-t border-line p-3">
          {batches.map((b) => <BatchSection key={b.id} batch={b} />)}
        </div>
      )}
    </aside>
  );
}

/** Every row, failures first: the person scrolls the whole batch (they asked to see all of it),
 *  and what needs their hand sits at the top. Thousands of rows are fine because UploadFileRow is
 *  memoised — a progress tick redraws one row, not the list. */
function orderedRows(items: UploadItem[]): { idx: number; item: UploadItem }[] {
  const indexed = items.map((item, idx) => ({ idx, item }));
  const failed = indexed.filter((r) => r.item.status === 'failed');
  return failed.length === 0 ? indexed : [...failed, ...indexed.filter((r) => r.item.status !== 'failed')];
}

function BatchSection({ batch }: { batch: UploadBatch }) {
  const failed = batch.items.filter((it) => it.status === 'failed').length;
  const rows = orderedRows(batch.items);
  return (
    <section aria-label={`Upload to ${batch.label}`} className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 px-0.5">
        <div className="truncate text-[12px] font-medium text-muted" title={batch.label}>{batch.label}</div>
        {!batch.running && (
          <div className="flex shrink-0 gap-1">
            {failed > 0 && (
              <Button size="xs" variant="outline" onClick={() => retryFailed(batch.id)}>
                Retry failed
              </Button>
            )}
            <Button size="xs" variant="ghost" onClick={() => dismissBatch(batch.id)} aria-label={`Dismiss upload to ${batch.label}`}>
              Dismiss
            </Button>
          </div>
        )}
      </div>
      <ul className={cn('space-y-1.5', batch.running && 'opacity-95')}>
        {rows.map(({ item, idx }) => (
          <UploadFileRow key={`${item.file.name}-${idx}`} item={item} running={batch.running} />
        ))}
      </ul>

    </section>
  );
}
